// S3 · the import history table (Module 02 §4 S3 "jobs table (kind, file,
// status, valid/warn/error counts, actions)").
//
// Server-side filtering by kind (the API gates a kind filter on that kind's
// import lane, §5), cursor pagination, and per row: open the detail screen, or
// discard a job that has not been loaded yet. Mobile collapses the table to
// cards, matching the Module 01 list idiom.

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { DataTable } from "../../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { formatTimestamp } from "../../../../lib/datetime";
import { requestIdOf } from "../../../../lib/request";
import { useDiscardImport, useImports, type ImportListQuery } from "../../hooks/onboarding-queries";
import { WizardDialog } from "../../components/wizard-dialog";
import { canDiscard, formatBytes, type ImportJob, type ImportKind } from "./import-model";
import { ImportProblemNotice } from "./import-problem-notice";
import { useImportLabels } from "./labels";

const PAGE_SIZE = 20;

export interface ImportJobsTableProps {
  /** Kinds offered in the filter — step 2 offers the catalogue lanes. */
  kinds: readonly ImportKind[];
  /** Lanes the caller may actually run (§7); others render read-only. */
  canRun: (kind: ImportKind) => boolean;
  /** One lane, always (spec 09 §4.18's Import customers page, P3-E2 PR
   * E2-2c): the list is always asked with this `kind` and the kind filter is
   * not drawn, so a page that runs one lane never lists another's jobs. */
  lockedKind?: ImportKind;
  /** Where Open goes. The wizard's S4 page by default; a page that hosts the
   * flow elsewhere (spec 09 §4.18) keeps the reader in its own area. */
  onOpen?: (jobId: string) => void;
}

export function ImportJobsTable({ kinds, canRun, lockedKind, onOpen }: ImportJobsTableProps) {
  const { t, i18n } = useTranslation();
  const labels = useImportLabels();
  const navigate = useNavigate();
  const [chosenKind, setKind] = useState<"" | ImportKind>("");
  const kind: "" | ImportKind = lockedKind ?? chosenKind;
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  const cursor = cursors[cursors.length - 1];
  const query: ImportListQuery = { limit: PAGE_SIZE };
  if (cursor !== undefined) query.cursor = cursor;
  if (kind !== "") query.kind = kind;

  const jobs = useImports(query);
  const items = jobs.data?.items ?? [];
  const nextCursor = jobs.data?.next_cursor ?? null;

  function open(id: string) {
    if (onOpen !== undefined) {
      onOpen(id);
      return;
    }
    void navigate({ to: "/onboarding/imports/$id", params: { id } });
  }

  return (
    <section className="import-jobs">
      <header className="import-section-head">
        <h3>{t("onboarding.catalogueStep.jobs.title")}</h3>
        {lockedKind !== undefined ? null : (
          <Field label={t("onboarding.catalogueStep.jobs.filterKind")}>
            <Select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as "" | ImportKind);
                setCursors([undefined]);
              }}
            >
              <option value="">{t("onboarding.catalogueStep.jobs.filterAny")}</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {labels.kindLabel(k)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </header>

      {jobs.isPending ? (
        <TableSkeleton rows={4} cols={6} label={t("onboarding.catalogueStep.jobs.loading")} />
      ) : jobs.error !== null ? (
        <ErrorState
          title={t("onboarding.catalogueStep.jobs.errorTitle")}
          // NEVER `error.message`: that is untranslated server/JS text, which a
          // Russian-speaking admin reads as their explanation (the i18n
          // rule). The request id below is the traceable part.
          detail={t("onboarding.catalogueStep.jobs.errorDetail")}
          retryLabel={t("onboarding.state.retry")}
          onRetry={() => void jobs.refetch()}
          requestId={requestIdOf(jobs.error)}
          requestIdLabel={t("onboarding.state.requestId")}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={t("onboarding.catalogueStep.jobs.emptyTitle")}
          body={t("onboarding.catalogueStep.jobs.emptyBody")}
        />
      ) : (
        <>
          {/* UX-6b: DataTable (D-219). This screen keeps its OWN dual render —
              the table hides at ≤768 (`.import-table-scroll`, imports.css) and
              the `.import-job-cards` list takes over — because the certified
              M02 responsive net pins exactly that geometry (import-job-row
              hidden / import-job-card visible at 412 px), so the primitive's
              built-in collapse never gets to apply here. */}
          <div className="import-table-scroll">
            <DataTable
              columns={[
                {
                  key: "kind",
                  header: t("onboarding.catalogueStep.jobs.col.kind"),
                  render: (job) => labels.kindLabel(job.kind),
                },
                {
                  key: "file",
                  header: t("onboarding.catalogueStep.jobs.col.file"),
                  render: (job) => (
                    <>
                      <button
                        type="button"
                        className="ds-lnk"
                        onClick={() => {
                          open(job.id);
                        }}
                      >
                        {job.file_name}
                      </button>
                      <span className="import-job-size">{formatBytes(job.file_size_bytes)}</span>
                    </>
                  ),
                },
                {
                  key: "status",
                  header: t("onboarding.catalogueStep.jobs.col.status"),
                  render: (job) => (
                    <span className={`badge status-${job.status}`}>
                      {labels.jobStatusLabel(job.status)}
                    </span>
                  ),
                },
                {
                  key: "rows",
                  header: t("onboarding.catalogueStep.jobs.col.rows"),
                  numeric: true,
                  render: (job) => job.total_rows,
                },
                {
                  key: "counts",
                  header: t("onboarding.catalogueStep.jobs.col.counts"),
                  render: (job) => <JobCounts job={job} />,
                },
                {
                  key: "created",
                  header: t("onboarding.catalogueStep.jobs.col.created"),
                  render: (job) => formatTimestamp(job.created_at, i18n.language),
                },
                {
                  key: "actions",
                  header: t("onboarding.catalogueStep.jobs.col.actions"),
                  actions: true,
                  render: (job) => <JobActions job={job} canRun={canRun(job.kind)} onOpen={open} />,
                },
              ]}
              rows={items}
              rowKey={(job) => job.id}
              empty={null}
              rowTestId="import-job-row"
              rowProps={(job) => ({ "data-status": job.status })}
            />
          </div>

          <ul className="import-job-cards">
            {items.map((job) => (
              <li key={job.id} className="import-job-card" data-testid="import-job-card">
                <div className="import-job-card-head">
                  <button
                    type="button"
                    className="ds-lnk"
                    onClick={() => {
                      open(job.id);
                    }}
                  >
                    {job.file_name}
                  </button>
                  <span className={`badge status-${job.status}`}>
                    {labels.jobStatusLabel(job.status)}
                  </span>
                </div>
                <p className="import-job-card-meta">
                  <span>{labels.kindLabel(job.kind)}</span>
                  <span>{formatTimestamp(job.created_at, i18n.language)}</span>
                </p>
                <JobCounts job={job} />
                <JobActions job={job} canRun={canRun(job.kind)} onOpen={open} />
              </li>
            ))}
          </ul>

          <nav className="pagination" aria-label={t("onboarding.catalogueStep.jobs.next")}>
            <Button
              data-testid="import-jobs-prev"
              disabled={cursors.length <= 1}
              onClick={() => {
                setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c));
              }}
            >
              {t("onboarding.catalogueStep.jobs.prev")}
            </Button>
            <span className="pagination-info">
              {t("onboarding.catalogueStep.jobs.showing", { count: items.length })}
            </span>
            <Button
              data-testid="import-jobs-next"
              disabled={nextCursor === null}
              onClick={() => {
                if (nextCursor !== null) setCursors((c) => [...c, nextCursor]);
              }}
            >
              {t("onboarding.catalogueStep.jobs.next")}
            </Button>
          </nav>
        </>
      )}
    </section>
  );
}

function JobCounts({ job }: { job: ImportJob }) {
  const { t } = useTranslation();
  return (
    <span className="import-job-counts">
      <span className="count-valid" title={t("onboarding.importShared.counters.valid")}>
        {job.valid_rows}
      </span>
      <span className="count-warning" title={t("onboarding.importShared.counters.warning")}>
        {job.warning_rows}
      </span>
      <span className="count-error" title={t("onboarding.importShared.counters.error")}>
        {job.error_rows}
      </span>
    </span>
  );
}

/** Per-row actions. The discard mutation is bound to THIS job's id, which is why
 * each row is its own component rather than a hook called in a loop. */
function JobActions({
  job,
  canRun,
  onOpen,
}: {
  job: ImportJob;
  canRun: boolean;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const discard = useDiscardImport(job.id);
  const [confirming, setConfirming] = useState(false);
  return (
    <span className="import-job-actions">
      <Button
        onClick={() => {
          onOpen(job.id);
        }}
      >
        {t("onboarding.catalogueStep.jobs.open")}
      </Button>
      {canRun && canDiscard(job.status) ? (
        <Button
          busy={discard.isPending}
          busyLabel={t("onboarding.catalogueStep.jobs.discarding")}
          onClick={() => {
            setConfirming(true);
          }}
        >
          {t("onboarding.catalogueStep.jobs.discard")}
        </Button>
      ) : null}
      {/* Named, styled, keyboard-safe and drivable in a walkthrough — the
          `window.confirm` this replaces was none of those (§4). */}
      {confirming ? (
        <WizardDialog
          testId="job-discard-confirm"
          destructive
          title={t("onboarding.importDetail.discard.confirmTitle")}
          confirmLabel={t("onboarding.catalogueStep.jobs.discard")}
          busy={discard.isPending}
          onConfirm={() => {
            setConfirming(false);
            discard.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        >
          <p>
            {t("onboarding.importDetail.discard.confirmBody", {
              file: job.file_name,
              rows: job.total_rows,
            })}
          </p>
        </WizardDialog>
      ) : null}
      {discard.error !== null ? <ImportProblemNotice error={discard.error} /> : null}
    </span>
  );
}
